import React, { ReactElement, useState } from 'react'

import styled from 'styled-components'

import { TrackClicked, TrackViewed } from 'components/trackers'
import { style } from 'theme'

import Modal from '../Modal'
import ModalButtons from '../Modal/buttons'
import ModalTypography from '../Modal/typography'
import { Heading } from '../typography'

const ModalMessage = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  overflow: auto;
`

export const ModalImage = styled.img<{ imageMax?: string }>`
  margin: 0 auto;
  ${({ imageMax }) =>
    imageMax === 'none'
      ? 'max-width: none'
      : imageMax === 'large'
        ? 'max-width: 200px'
        : 'max-width: 104px'};
`

const ModalImageContainer = styled('div')<{
  imageMax?: string
  imageBorder?: boolean
}>`
  margin: 0 auto 24px;

  ${(props) =>
    props.imageBorder &&
    `
      width: 104px;
      border: 26px solid ${props.theme.colors.ivory};
      border-radius: 50%;
      display:flex;
      justify-content: center;
      align-items: center;

      img {
        width: 52px;
      }
    `}

  ${({ imageMax }) =>
    imageMax === 'none'
      ? 'max-width: none'
      : imageMax === 'large'
        ? 'max-width: 200px'
        : 'max-width: 104px'}

  @media screen and (max-height: ${style((theme) => theme.breakpoints.sm)}px) {
    /* responsive landscape orientation */
    width: calc(104px / 5.5);
    height: calc(104px / 5.5);
    margin: 0 auto 10px;
  }
`

const ModalHeader = styled(Heading)`
  margin-bottom: 8px;
  text-align: center;
`

const Anchor = styled.a`
  width: 100%;
  text-decoration: none;

  &:not(:only-child):not(:last-child) {
    border-right: 1px solid ${style((theme) => theme.colors.ivory)};
  }
`

const ActionImage = styled.img`
  margin: 7.5%;
`

export enum ModalActionTypes {
  danger = 'danger',
  primary = 'primary',
  secondary = 'secondary',
}

export type Handler = (payload?: any) => void

export type ModalAction = {
  handler?: Handler
  href?: string
  eventFields?: object
  label: string | ReactElement
  type: ModalActionTypes
  testId?: string
  icon?: string
}

export enum ButtonStyle {
  col,
  row,
}

export type Props = {
  actions: ModalAction[]
  buttonStyle?: ButtonStyle
  body?: string | React.ReactNode | (() => React.ReactNode)
  imageMax?: 'none' | 'large'
  eventFields?: object
  header?: string | React.ReactNode | (() => React.ReactNode)
  image?: string
  imageAlt?: string
  imageBorder?: boolean
  isOpen: boolean
  className?: string
  onRequestClose?: () => void
  title?: string | React.ReactNode | (() => React.ReactNode)
  closeOnOutsideClick?: boolean
  onCloseAnimationComplete?: () => void
  'data-testid'?: string
}

const actionTypeMap: {
  [key: string]: React.FC<{ 'data-testid': string; onClick?: Handler }>
} = {
  danger: ModalButtons.Danger as any,
  primary: ModalButtons.Primary as any,
  secondary: ModalButtons.Secondary as any,
}

const Buttons: React.FC<{ buttonStyle?: ButtonStyle }> = ({
  buttonStyle = ButtonStyle.row,
  children,
}) => (
  <>
    {buttonStyle === ButtonStyle.row ? (
      <ModalButtons.Row>{children}</ModalButtons.Row>
    ) : (
      <ModalButtons.Col>{children}</ModalButtons.Col>
    )}
  </>
)

const renderContent = (
  prop: string | React.ReactNode | (() => React.ReactNode),
) => (typeof prop === 'function' ? prop() : prop)

// Necessary for modals that may open multiple times on a single page
const ConditionalTrackViewed: React.FC<{ track: boolean; fields?: object }> = ({
  track,
  fields,
  children,
}) =>
  track && fields ? (
    <TrackViewed fields={fields} multiple>
      {children}
    </TrackViewed>
  ) : (
    <>{children}</>
  )

// Prevent the wrapping tracker component from breaking the flex layout
const TrackedButton = ModalButtons.Button.withComponent(
  TrackClicked,
) as any as typeof TrackClicked

const ConditionalTrackClicked: React.FC<{ fields?: object; label: string }> = ({
  fields,
  children,
  label,
}) =>
  fields ? (
    <TrackedButton fields={{ ...fields, cta_title: label }}>
      {children}
    </TrackedButton>
  ) : (
    <>{children}</>
  )

const ConfirmationModal = (props: Props) => {
  const [loaded, setLoaded] = useState(false)
  return (
    <Modal
      className={props.className}
      closeOnEsc={true}
      closeOnOutsideClick={props.closeOnOutsideClick || true}
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      data-testid={props['data-testid']}
      onCloseAnimationComplete={props.onCloseAnimationComplete}
    >
      {() => (
        <>
          <div style={!props.image || loaded ? {} : { display: 'none' }}>
            <ConditionalTrackViewed
              track={props.isOpen}
              fields={props.eventFields}
            >
              <ModalMessage>
                {props.image && (
                  <ModalImageContainer
                    imageBorder={props.imageBorder}
                    imageMax={props.imageMax}
                  >
                    <ModalImage
                      alt={props.imageAlt ? props.imageAlt : ''}
                      imageMax={props.imageMax}
                      src={props.image}
                      onLoad={() => setLoaded(true)}
                    />
                  </ModalImageContainer>
                )}
                {props.title && (
                  <ModalTypography.Title>
                    {renderContent(props.title)}
                  </ModalTypography.Title>
                )}
                {props.header && (
                  <ModalHeader>{renderContent(props.header)}</ModalHeader>
                )}
                {props.body && (
                  <ModalTypography.Body>
                    {renderContent(props.body)}
                  </ModalTypography.Body>
                )}
              </ModalMessage>
            </ConditionalTrackViewed>
          </div>

          <Buttons buttonStyle={props.buttonStyle}>
            {props.actions?.map((action, index) => {
              const Button = actionTypeMap[action.type]
              return (
                <ConditionalTrackClicked
                  fields={action.eventFields}
                  label={String(action.label)}
                  key={String(action.label)}
                >
                  {action.href ? (
                    <Anchor
                      href={action.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Button data-testid={action.testId || `button_${index}`}>
                        {action.label}
                      </Button>
                    </Anchor>
                  ) : (
                    <Button
                      onClick={action.handler}
                      data-testid={action.testId || `button_${index}`}
                    >
                      {action.icon && <ActionImage alt="" src={action.icon} />}
                      {action.label}
                    </Button>
                  )}
                </ConditionalTrackClicked>
              )
            })}
          </Buttons>
        </>
      )}
    </Modal>
  )
}
export default ConfirmationModal
